import { getLocationLabel } from '../i18n';
import { TV_BROADCAST_PERIODS } from '../data/tvBroadcastPeriod';
import { TV_DISPLAY_HOURS } from '../data/tvDemoDisplay';
import {
  calibrateHourlyMapForTvSpot,
} from './tvSpotCalibration';
import {
  buildDaySlots,
  buildTwoHourSlots,
  dailyAvgCongestion,
  findBusiestPartName,
  findBusiestPartFromHourly,
  findBusiestTwoHourLabel,
  rawToCongestionTier,
} from './tvCongestionDisplay';
import { TV_PAST_WEEK_LOCATION_IDS, getTvPastWeekLocationLabel } from '../data/tvDemoLocations';
import { TV_STATION_ID, TV_STATION_IMAGE, getTvSpotPanelTitles } from '../data/tvDemoDisplay';
import { tvLog, tvWarn } from './tvDemoDebug';
import {
  buildReviewHeadline,
  reviewHoursForToday,
  REVIEW_NOON_HOUR,
  shouldUseTodayReview,
} from './tvReviewCopy';

/** API・集計用（7〜22時） */
export const TV_HOURS = Array.from({ length: 16 }, (_, i) => i + 7);

/** テレビ表の列・行（表示時間帯） */
export const TV_MATRIX_HOURS = TV_DISPLAY_HOURS;

const hourLabel = (h) => `${h}時`;

export const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const parseIsoDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const addDaysIso = (iso, days) => {
  const dt = parseIsoDate(iso);
  dt.setDate(dt.getDate() + days);
  return toIsoDate(dt);
};

export const formatJaDateLabel = (iso) => {
  const dt = parseIsoDate(iso);
  const w = ['日', '月', '火', '水', '木', '金', '土'][dt.getDay()];
  return `${dt.getFullYear()}年${dt.getMonth() + 1}月${dt.getDate()}日（${w}）`;
};

const todayIso = () => toIsoDate(new Date());

/** 地点の短い行ラベル（ヒートマップ左列用） */
export const getLocationShortLabel = (locationId, language = 'ja') => {
  const full = getLocationLabel(locationId, language);
  const shortMap = {
    'old-town': '古町',
    station: '駅前',
    gyouzinbashi: '行司橋',
    omotesando: '表参道',
    yottekan: 'よって館',
    honmachi4: '本町4',
    honmachi3: '本町3',
    honmachi2: '本町2',
    kokubunjidori: '国分寺',
    yasukawadori: '安川',
    jinnya: '陣屋',
    nakabashi: '中橋',
  };
  return shortMap[locationId] || full.replace(/商店街$/, '').slice(0, 6);
};

const shortDayLabel = (iso, baseIso, dayIndex) => {
  const dt = parseIsoDate(iso);
  const m = dt.getMonth() + 1;
  const day = dt.getDate();
  const w = ['日', '月', '火', '水', '木', '金', '土'][dt.getDay()];
  if (iso === baseIso) {
    return baseIso === todayIso() ? '今日' : `${m}/${day}`;
  }
  if (dayIndex === 1 && baseIso === todayIso()) return '明日';
  return `${w}${m}/${day}`;
};

/** graph dti の1日分 → hour → congestion */
export const hourlyMapFromCalendarDay = (day, locationId, targetIso) => {
  if (!day?.hours?.length) {
    tvWarn(`dti: 時間データなし (${locationId}, ${targetIso})`, day);
    return null;
  }
  const map = {};
  let count = 0;
  day.hours.forEach((h) => {
    if (h.hour >= TV_HOURS[0] && h.hour <= TV_HOURS[TV_HOURS.length - 1]) {
      const c = h.congestion || 0;
      map[h.hour] = c;
      if (c > 0) count += 1;
    }
  });
  tvLog(`dti: ${locationId} @ ${targetIso}`, {
    date: day.date,
    hoursWithCongestion: count,
    source: day.hours.find((h) => h.source)?.source,
  });
  return count > 0 ? map : null;
};

/** congestion API の予測時間別（未来・当日予測向け） */
export const hourlyMapFromPrediction = (detailData, targetIso) => {
  const entry = detailData?.data?.today_prediction_hourly;
  if (!entry?.data_available || !Array.isArray(entry.hourly_congestion)) {
    return null;
  }
  if (entry.date && entry.date !== targetIso) {
    tvWarn('prediction: 日付不一致', { entryDate: entry.date, targetIso });
  }
  const map = {};
  let count = 0;
  entry.hourly_congestion.forEach((h) => {
    if (h.hour >= TV_HOURS[0] && h.hour <= TV_HOURS[TV_HOURS.length - 1]) {
      const c = h.congestion || 0;
      map[h.hour] = c;
      if (c > 0) count += 1;
    }
  });
  tvLog('prediction hourly', { targetIso, hoursWithCongestion: count });
  return count > 0 ? map : null;
};

/** 基準日の時間別：dti 実績/予測 → なければ congestion 予測 */
export const resolveHourlyForDate = (calendarDay, detailData, locationId, targetIso) => {
  const fromDti = hourlyMapFromCalendarDay(calendarDay, locationId, targetIso);
  if (fromDti) return { map: fromDti, source: 'dti_cog' };
  const fromPred = hourlyMapFromPrediction(detailData, targetIso);
  if (fromPred) return { map: fromPred, source: 'today_prediction_hourly' };
  tvWarn(`時間別データなし: ${locationId}`, { targetIso });
  return { map: null, source: 'none' };
};

export const findCalendarDay = (calendarData, iso) => {
  if (!Array.isArray(calendarData)) return null;
  return calendarData.find((d) => d?.date === iso) || null;
};

/** 7日分がまたがる年月のリスト [{year, month}, ...] */
export const monthsSpanningRange = (baseIso, days = 7) => {
  const keys = new Set();
  for (let i = 0; i < days; i += 1) {
    const dt = parseIsoDate(addDaysIso(baseIso, i));
    keys.add(`${dt.getFullYear()}-${dt.getMonth() + 1}`);
  }
  return [...keys].map((k) => {
    const [y, m] = k.split('-').map(Number);
    return { year: y, month: m };
  });
};

/** 振り返り用に前日分も含めた取得月リスト */
export const monthsSpanningRangeWithLookback = (baseIso, forwardDays = 7, backDays = 1) => {
  const keys = new Set();
  for (let i = -backDays; i < forwardDays; i += 1) {
    const dt = parseIsoDate(addDaysIso(baseIso, i));
    keys.add(`${dt.getFullYear()}-${dt.getMonth() + 1}`);
  }
  return [...keys].map((k) => {
    const [y, m] = k.split('-').map(Number);
    return { year: y, month: m };
  });
};

export const mergeCalendarData = (arrays) => {
  const byDate = new Map();
  arrays.flat().forEach((day) => {
    if (day?.date) byDate.set(day.date, day);
  });
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
};

export const buildTodayMatrix = (locationIds, locationHourlyMaps, language = 'ja') => {
  const rowLabels = locationIds.map((id) => getLocationShortLabel(id, language));
  const colLabels = TV_MATRIX_HOURS.map(hourLabel);
  const cells = locationIds.map((id) => {
    const hourly = locationHourlyMaps[id];
    return TV_MATRIX_HOURS.map((hour) => hourly?.[hour] || 0);
  });
  return {
    rowLabels,
    colLabels,
    cells,
    rowHeader: '商店街',
    colHeader: null,
  };
};

export const findTodayPeak = (locationId, locationHourlyMaps, language = 'ja') => {
  const hourly = locationHourlyMaps[locationId];
  if (!hourly) {
    return { highlightArea: getLocationLabel(locationId, language), peakLabel: 'データなし' };
  }
  let max = 0;
  let peakHours = [];
  TV_MATRIX_HOURS.forEach((hour) => {
    const v = hourly[hour] || 0;
    if (v > max) {
      max = v;
      peakHours = [hour];
    } else if (v === max && max > 0) {
      peakHours.push(hour);
    }
  });
  if (!peakHours.length) {
    return {
      highlightArea: getLocationLabel(locationId, language),
      peakLabel: '終日落ち着き',
    };
  }
  const start = Math.min(...peakHours);
  const end = Math.max(...peakHours);
  const peakLabel = start === end ? `${start}時ピーク` : `${start}〜${end}時ピーク`;
  return {
    highlightArea: getLocationLabel(locationId, language),
    peakLabel,
  };
};

export const buildDateTimeMatrixFromCalendar = (calendarData, baseIso, days = 7) => {
  if (!Array.isArray(calendarData) || calendarData.length === 0) {
    tvWarn('週間マトリクス: calendarData 空', { baseIso });
    return null;
  }
  const endIso = addDaysIso(baseIso, days - 1);
  const sorted = [...calendarData]
    .filter((d) => d?.date && d.date >= baseIso && d.date <= endIso)
    .sort((a, b) => a.date.localeCompare(b.date));

  tvLog('週間マトリクス', {
    baseIso,
    endIso,
    matchedDays: sorted.map((d) => d.date),
    totalCalendarDays: calendarData.length,
  });

  if (sorted.length === 0) return null;

  const colLabels = sorted.map((d, i) => shortDayLabel(d.date, baseIso, i));
  const rowLabels = TV_MATRIX_HOURS.map(hourLabel);
  const cells = rowLabels.map((_, hi) => {
    const hour = TV_MATRIX_HOURS[hi];
    return sorted.map((day) => {
      const hourData = day.hours?.find((h) => h.hour === hour);
      return hourData?.congestion || 0;
    });
  });

  return {
    rowLabels,
    colLabels,
    cells,
    rowHeader: '時間',
    colHeader: null,
  };
};

export const buildLastYearMatrixFromCalendar = (calendarData, baseIso, days = 7) => {
  if (!Array.isArray(calendarData)) return null;

  const targetDates = [];
  for (let i = 0; i < days; i += 1) {
    const iso = addDaysIso(baseIso, i);
    const dt = parseIsoDate(iso);
    dt.setFullYear(dt.getFullYear() - 1);
    targetDates.push(toIsoDate(dt));
  }

  const byDate = Object.fromEntries(calendarData.filter((d) => d?.date).map((d) => [d.date, d]));
  const sorted = targetDates.map((date) => byDate[date]).filter(Boolean);

  tvLog('前年マトリクス', {
    targetDates,
    matched: sorted.map((d) => d.date),
  });

  if (sorted.length === 0) return null;

  const colLabels = sorted.map((d, i) => {
    const dt = parseIsoDate(d.date);
    const m = dt.getMonth() + 1;
    const day = dt.getDate();
    const w = ['日', '月', '火', '水', '木', '金', '土'][dt.getDay()];
    return i === 0 ? `前年\n${m}/${day}` : `${w}${m}/${day}`;
  });

  const rowLabels = TV_MATRIX_HOURS.map(hourLabel);
  const cells = rowLabels.map((_, hi) => {
    const hour = TV_MATRIX_HOURS[hi];
    return sorted.map((day) => {
      const hourData = day.hours?.find((h) => h.hour === hour);
      return hourData?.congestion || 0;
    });
  });

  return {
    rowLabels,
    colLabels,
    cells,
    rowHeader: '時間',
    colHeader: null,
  };
};

export const buildWeeklySummary = (matrix, focusLocationName) => {
  if (!matrix?.cells?.length) {
    return `${focusLocationName}：この期間のデータがありません`;
  }
  const lastCol = matrix.cells[0]?.length - 1;
  let weekendHigh = false;
  for (let c = Math.max(0, lastCol - 1); c <= lastCol; c += 1) {
    matrix.cells.forEach((row) => {
      if ((row[c] || 0) >= 14) weekendHigh = true;
    });
  }
  return weekendHigh
    ? '週末にかけて午後の混雑が強まる見込み'
    : '週の前半は落ち着き、後半にやや増えそうです';
};

/** 週間マトリクスで午後帯（13〜16時）の平均混雑 */
const afternoonAvg = (matrix) => {
  if (!matrix?.cells?.length) return 0;
  const vals = [];
  matrix.cells.forEach((row, ri) => {
    const hour = TV_MATRIX_HOURS[ri];
    if (hour >= 13 && hour <= 16) {
      row.forEach((v) => {
        if (v > 0) vals.push(v);
      });
    }
  });
  if (!vals.length) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

/**
 * 画面4：天気予報風コメント＋事業者向け行動＋目的ベースダッシュボード宣伝
 */
export const buildActionScreenData = ({
  targetIso,
  focusLocationName,
  peak,
  weeklySummary,
  weeklyMatrix,
  review,
  period = TV_BROADCAST_PERIODS.MORNING,
  todaySummary = '',
}) => {
  const dateLabel = formatJaDateLabel(targetIso);
  const peakTime = peak.peakLabel.replace('ピーク', '').trim() || '—';
  const pmAvg = afternoonAvg(weeklyMatrix);

  const businessActions = [
    `ピークの${peakTime}前後は、店頭や案内を手厚くすると効果的です。`,
    '雨の日・イベント日は、混雑の出方が変わるため、当日の確認がおすすめです。',
  ];

  if (period === TV_BROADCAST_PERIODS.NOON) {
    return {
      panelTitle: '今日のポイントとお店の工夫',
      reviewLabel: review?.label || '',
      reviewSummary: review?.headline || '',
      weeklyCaption: peakTime !== '—' ? `このあと${peakTime}ごろがピークの見込みです` : '',
      forecast: {
        heading: '予報コメント',
        items: [
          `${focusLocationName}は${peakTime}ごろに人通りが最も増えやすい見込みです。`,
          pmAvg >= 14
            ? '午後は全体的に混雑しやすく、ピーク前後の対応が重要です。'
            : '午後にかけて徐々に人通りが増える見込みです。',
        ],
      },
      actions: {
        heading: 'お店での工夫',
        items: businessActions,
      },
    };
  }

  if (period === TV_BROADCAST_PERIODS.EVENING) {
    const forecastComments = [
      `${dateLabel}の${focusLocationName}は、${peakTime}ごろに人通りが最も増えました。`,
    ];

    if (pmAvg >= 14) {
      forecastComments.push('午後の時間帯は全体的に混雑していました。');
    } else if (pmAvg >= 10) {
      forecastComments.push('午前は落ち着き、午後から徐々に人通りが増えました。');
    } else {
      forecastComments.push('一日を通して大きな混雑は少なく、平穏に営業しやすい一日でした。');
    }

    return {
      panelTitle: 'きょうの振り返りとお店の工夫',
      reviewLabel: review?.label || '',
      reviewSummary: review?.headline || '',
      weeklyCaption: todaySummary || weeklySummary || '',
      forecast: {
        heading: '予報コメント',
        items: forecastComments,
      },
      actions: {
        heading: 'お店での工夫',
        items: businessActions,
      },
    };
  }

  const forecastComments = [
    `${dateLabel}の${focusLocationName}は、${peakTime}ごろに人通りが最も増えやすい見込みです。`,
  ];

  if (pmAvg >= 14) {
    forecastComments.push('午後の時間帯は全体的に混雑しやすく、特に週末はにぎわいが続きそうです。');
  } else if (pmAvg >= 10) {
    forecastComments.push('午前は落ち着き、午後から徐々に人通りが増える一日になりそうです。');
  } else {
    forecastComments.push('一日を通して大きな混雑は少なめで、平穏に営業しやすい見込みです。');
  }

  return {
    panelTitle: '今日のポイントとお店の工夫',
    reviewLabel: review?.label || '',
    reviewSummary: review?.headline || '',
    weeklyCaption: weeklySummary || 'きょうは終日おおむね穏やかな見込みです',
    forecast: {
      heading: '予報コメント',
      items: forecastComments,
    },
    actions: {
      heading: 'お店での工夫',
      items: businessActions,
    },
  };
};

export const buildIntroHeadline = (peak, busiestPart, hourlyMap) => {
  const area = formatForecastArea(peak.highlightArea || '市街');
  const dayParts = ['朝', '昼', '夕'];
  const partFromPeak = findBusiestPartFromHourly(hourlyMap);
  const part = partFromPeak && dayParts.includes(partFromPeak) ? partFromPeak : busiestPart;

  if (part && dayParts.includes(part)) {
    return `きょうは${area}、${part}に人通りが増える見込みです。`;
  }

  const time = peak.peakLabel.replace(/ピーク$/, '').trim();
  if (time && time !== 'データなし' && time !== '終日落ち着き') {
    return `きょうは${area}、${time}ごろに人通りが増える見込みです。`;
  }

  return `きょうは${area}、終日おおむね穏やかな見込みです。`;
};

const formatForecastArea = (name) => {
  if (!name) return '市街付近';
  if (name.includes('付近') || name.includes('エリア')) return name;
  if (name === '駅前' || name.endsWith('駅前')) return '高山駅前付近';
  if (name.endsWith('商店街')) return name.replace(/商店街$/, '商店街付近');
  return `${name}付近`;
};

/** きょう1日のまとめ（夕方放送は past=true で過去形） */
export const buildTodaySummaryText = (hourlyMap, past = false) => {
  const slots = buildTwoHourSlots(hourlyMap);
  const peakTime = findBusiestTwoHourLabel(slots);
  const maxRaw = slots.reduce((m, s) => Math.max(m, s.raw || 0), 0);
  const dailyAvg = dailyAvgCongestion(hourlyMap);

  if (peakTime && maxRaw >= 14) {
    return past
      ? `きょうは${peakTime}ごろに人通りが最も集まりました`
      : `きょうは${peakTime}ごろに人通りが最も集まりやすい見込みです`;
  }
  if (peakTime && maxRaw >= 10) {
    return past
      ? `きょうは${peakTime}前後がピークでした`
      : `きょうは${peakTime}前後がピークの見込みです`;
  }
  if (dailyAvg >= 12) {
    return past
      ? 'きょうは一日を通してにぎわっていました'
      : 'きょうは一日を通してにぎわいやすい見込みです';
  }
  if (dailyAvg >= 8) {
    return past
      ? 'きょうは一日を通しておおむねふつうでした'
      : 'きょうは一日を通しておおむねふつうの見込みです';
  }
  return past
    ? 'きょうは一日を通しておおむね穏やかでした'
    : 'きょうは一日を通しておおむね穏やかな見込みです';
};

/** 画面1：予報一文（放送時間帯で出し分け） */
export const buildIntroPanelData = ({
  segmentTitle,
  targetIso,
  peak,
  busiestPart,
  focusHourlyMap,
  period = TV_BROADCAST_PERIODS.MORNING,
  stationTodayCaption,
  todaySummary,
}) => {
  let outlookHeadline = buildIntroHeadline(peak, busiestPart, focusHourlyMap);

  if (period === TV_BROADCAST_PERIODS.NOON && stationTodayCaption) {
    outlookHeadline = stationTodayCaption;
  } else if (period === TV_BROADCAST_PERIODS.EVENING && todaySummary) {
    outlookHeadline = todaySummary;
  }

  return {
    title: segmentTitle,
    dateLabel: formatJaDateLabel(targetIso),
    outlookHeadline,
    ticker: '',
  };
};

/** 駅前の振り返り（今日ここまで / きのう / 基準日） */
export const buildStationReview = ({
  targetIso,
  calendarData,
  detailData,
  todayHourlyMap,
  locationId = TV_STATION_ID,
  locationName = '駅前',
  now = new Date(),
}) => {
  const isLiveToday = targetIso === toIsoDate(now);
  const useToday = shouldUseTodayReview(targetIso, now);
  const yesterdayIso = addDaysIso(targetIso, -1);
  const yesterdayMap = resolveHourlyForIso(calendarData, detailData, locationId, yesterdayIso);
  const todayMap = todayHourlyMap || {};

  if (isLiveToday && useToday) {
    const hours = reviewHoursForToday(now, targetIso);
    const dayWord = now.getHours() < REVIEW_NOON_HOUR + 3 ? 'きょう午前は' : 'きょうは';
    return {
      label: 'きょうここまで',
      headline: buildReviewHeadline(todayMap, hours, {
        dayWord,
        fallback: 'きょうここまでのデータは少なめです',
      }),
      locationName,
    };
  }

  if (isLiveToday) {
    return {
      label: 'きのうの振り返り',
      headline: buildReviewHeadline(yesterdayMap, [...TV_DISPLAY_HOURS], {
        dayWord: 'きのうは',
        fallback: 'きのうのデータは確認中です',
      }),
      locationName,
    };
  }

  const pastMap = resolveHourlyForIso(calendarData, detailData, locationId, targetIso);
  const dt = parseIsoDate(targetIso);
  const w = ['日', '月', '火', '水', '木', '金', '土'][dt.getDay()];
  return {
    label: `${w}${dt.getMonth() + 1}/${dt.getDate()}の振り返り`,
    headline: buildReviewHeadline(pastMap, [...TV_DISPLAY_HOURS], {
      dayWord: 'この日は',
      fallback: 'この日のデータは確認中です',
    }),
    locationName,
  };
};

export const buildOverviewLocations = (locationIds, language = 'ja') =>
  locationIds.map((id) => getLocationShortLabel(id, language));

const hourlyFromCalendar = (calendarData, iso, locationId) => {
  const day = findCalendarDay(calendarData, iso);
  return hourlyMapFromCalendarDay(day, locationId, iso) || {};
};

export const buildLaterTrend = (calendarData, baseIso, locationId) => {
  const weekendVals = [];
  for (let i = 2; i < 7; i += 1) {
    const iso = addDaysIso(baseIso, i);
    const map = hourlyFromCalendar(calendarData, iso, locationId);
    [12, 13, 14, 15, 16].forEach((h) => {
      if (map[h] > 0) weekendVals.push(map[h]);
    });
  }
  if (!weekendVals.length) {
    return 'この先はデータ確認中です';
  }
  const avg = weekendVals.reduce((a, b) => a + b, 0) / weekendVals.length;
  const dt = parseIsoDate(addDaysIso(baseIso, 5));
  const isWeekendSoon = dt.getDay() === 0 || dt.getDay() === 6;
  if (avg >= 14 || isWeekendSoon) {
    return '週末に向けて人通りが増える見込み';
  }
  if (avg >= 10) {
    return '後半にかけてやや増える見込み';
  }
  return '大きな変化は少なめの見込み';
};

export const buildLocationCaption = (locationName, todaySlots) => {
  const part = findBusiestPartName(todaySlots);
  if (!part) {
    return `${locationName}は一日を通して落ち着いた見込みです`;
  }
  return `${locationName}は${part}ごろに人通りが集まりやすい見込みです`;
};

export const buildLocationForecast = ({
  locationId,
  locationName,
  imageSrc,
  calendarData,
  detailData,
  baseIso,
}) => {
  const todayIso = baseIso;
  const tomorrowIso = addDaysIso(baseIso, 1);

  const todayDay = findCalendarDay(calendarData, todayIso);
  const { map: todayMap } = resolveHourlyForDate(todayDay, detailData, locationId, todayIso);

  const tomorrowDay = findCalendarDay(calendarData, tomorrowIso);
  const tomorrowMap =
    hourlyMapFromCalendarDay(tomorrowDay, locationId, tomorrowIso) ||
    hourlyFromCalendar(calendarData, tomorrowIso, locationId);

  const todaySlots = buildDaySlots(todayMap);
  const tomorrowSlots = buildDaySlots(tomorrowMap);

  return {
    locationId,
    locationName,
    imageSrc,
    today: { label: '今日', slots: todaySlots },
    tomorrow: { label: '明日', slots: tomorrowSlots },
    laterTrend: buildLaterTrend(calendarData, baseIso, locationId),
    caption: buildLocationCaption(locationName, todaySlots),
  };
};

const resolveHourlyForIso = (calendarData, detailData, locationId, iso) => {
  const day = findCalendarDay(calendarData, iso);
  const { map } = resolveHourlyForDate(day, detailData, locationId, iso);
  return map || hourlyMapFromCalendarDay(day, locationId, iso) || {};
};

const shortDateChipLabel = (iso) => {
  const dt = parseIsoDate(iso);
  const w = ['日', '月', '火', '水', '木', '金', '土'][dt.getDay()];
  return `${w}${dt.getMonth() + 1}/${dt.getDate()}`;
};

const pastDayRowLabel = (iso, baseIso) => {
  if (iso === addDaysIso(baseIso, -1)) return 'きのう';
  return shortDateChipLabel(iso);
};

export const buildStationTodayPanel = ({
  hourlyMap,
  locationName = '駅前',
  locationId = TV_STATION_ID,
  imageSrc = TV_STATION_IMAGE,
  period = TV_BROADCAST_PERIODS.MORNING,
  panelTitle,
  baseIso,
}) => {
  const titles = getTvSpotPanelTitles(locationId);
  const displayMap = calibrateHourlyMapForTvSpot(locationId, hourlyMap, { iso: baseIso });
  const slots = buildTwoHourSlots(displayMap);
  const peakTime = findBusiestTwoHourLabel(slots);
  const defaultCaption = peakTime
    ? `このあと${peakTime}ごろに人通りが増えやすい見込みです`
    : 'このあとは落ち着いた人通りの見込みです';

  let caption = defaultCaption;
  if (period === TV_BROADCAST_PERIODS.MORNING) {
    const part = findBusiestPartFromHourly(displayMap);
    caption = part
      ? `きょうは${part}に人通りが増えやすい見込みです`
      : 'きょうは終日おおむね穏やかな見込みです';
  } else if (period === TV_BROADCAST_PERIODS.EVENING) {
    caption = buildTodaySummaryText(displayMap, true);
  }

  return {
    locationId,
    locationName,
    imageSrc,
    panelTitle: panelTitle || titles.today,
    slots,
    caption,
  };
};

const buildSpotDayOffsetCaption = (dayOffset, peakTime) => {
  if (dayOffset === 2) {
    return peakTime
      ? `あさっては${peakTime}ごろに人通りが増えやすい見込みです`
      : 'あさっては終日おおむね穏やかな見込みです';
  }
  return peakTime
    ? `あしたは${peakTime}ごろに人通りが増えやすい見込みです`
    : 'あしたは終日おおむね穏やかな見込みです';
};

/** 地点別・指定日の2時間刻み（dayOffset: 1=明日, 2=明後日） */
export const buildStationDayOffsetPanel = ({
  calendarData,
  detailData,
  baseIso,
  dayOffset = 1,
  locationName = '駅前',
  locationId = TV_STATION_ID,
  imageSrc = TV_STATION_IMAGE,
  panelTitle,
}) => {
  const targetDayIso = addDaysIso(baseIso, dayOffset);
  const map = calibrateHourlyMapForTvSpot(
    locationId,
    resolveHourlyForIso(calendarData, detailData, locationId, targetDayIso),
    { iso: targetDayIso },
  );
  const slots = buildTwoHourSlots(map);
  const peakTime = findBusiestTwoHourLabel(slots);
  const titles = getTvSpotPanelTitles(locationId);
  const defaultTitle =
    dayOffset === 2 ? titles.dayAfter : titles.tomorrow;

  return {
    locationId,
    locationName,
    imageSrc,
    panelTitle: panelTitle || defaultTitle,
    slots,
    caption: buildSpotDayOffsetCaption(dayOffset, peakTime),
  };
};

/** 地点別・明日の2時間刻み */
export const buildStationTomorrowPanel = (params) =>
  buildStationDayOffsetPanel({ ...params, dayOffset: 1 });

/** 地点別・明後日の2時間刻み（夜：朝の「明日」に相当） */
export const buildStationDayAfterPanel = (params) =>
  buildStationDayOffsetPanel({ ...params, dayOffset: 2 });

const avgSlotsCongestion = (slots = []) => {
  if (!slots.length) return 0;
  const sum = slots.reduce((acc, slot) => acc + (slot.raw || 0), 0);
  return sum / slots.length;
};

const buildPastWeekSummary = (pastDays = []) => {
  if (!pastDays.length) return '先週のデータは確認中です';

  const allRaws = pastDays.flatMap((d) => (d.slots || []).map((s) => s.raw || 0));
  const maxRaw = allRaws.length ? Math.max(...allRaws) : 0;
  const avgs = pastDays.map((day) => avgSlotsCongestion(day.slots));
  const firstHalf = avgs.slice(0, Math.ceil(avgs.length / 2));
  const secondHalf = avgs.slice(Math.ceil(avgs.length / 2));
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const diff = avg(secondHalf) - avg(firstHalf);
  const lastDaysMax = Math.max(
    ...pastDays.slice(-2).flatMap((d) => (d.slots || []).map((s) => s.raw || 0)),
    0,
  );

  if (maxRaw >= 16 || lastDaysMax >= 16) {
    return '週末などに人通りが増える日がありました';
  }
  if (maxRaw >= 14) {
    return 'にぎわいのピークが見られた日もありました';
  }
  if (diff >= 2) return '後半にかけて人通りが増える流れでした';
  if (diff <= -2) return '後半にかけて落ち着く流れでした';
  return '先週はおおむね穏やかな流れでした';
};

const buildPastWeekOverviewSummary = (locationRows = []) => {
  if (!locationRows.length) return '先週のデータは確認中です';

  const dayAvgs = [];
  const colCount = locationRows[0]?.days?.length || 0;
  for (let ci = 0; ci < colCount; ci += 1) {
    const vals = locationRows
      .map((row) => row.days[ci]?.raw || 0)
      .filter((v) => v > 0);
    if (vals.length) {
      dayAvgs.push(vals.reduce((a, b) => a + b, 0) / vals.length);
    }
  }

  const allRaws = locationRows.flatMap((row) => row.days.map((d) => d.raw || 0)).filter((v) => v > 0);
  const maxRaw = allRaws.length ? Math.max(...allRaws) : 0;
  const firstHalf = dayAvgs.slice(0, Math.ceil(dayAvgs.length / 2));
  const secondHalf = dayAvgs.slice(Math.ceil(dayAvgs.length / 2));
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const diff = avg(secondHalf) - avg(firstHalf);
  const weekendAvg = avg(dayAvgs.slice(-2));

  if (maxRaw >= 16 || weekendAvg >= 14) {
    return '週末などに人通りが増える日がありました';
  }
  if (maxRaw >= 13) {
    return 'にぎわいのピークが見られた日もありました';
  }
  if (diff >= 1.5) return '後半にかけて人通りが増える流れでした';
  if (diff <= -1.5) return '後半にかけて落ち着く流れでした';
  return '先週はおおむね穏やかな流れでした';
};

const buildYesterdayOverviewSummary = (locationRows = []) => {
  if (!locationRows.length) return 'きのうのデータは確認中です';

  const allSlots = locationRows.flatMap((row) => row.slots || []);
  const raws = allSlots.map((s) => s.raw || 0).filter((v) => v > 0);
  if (!raws.length) return 'きのうのデータは確認中です';

  const maxRaw = Math.max(...raws);
  const peakLabel = findBusiestTwoHourLabel(allSlots);

  if (maxRaw >= 16) {
    return peakLabel
      ? `きのうは${peakLabel}ごろに人通りが増えました`
      : 'きのうはにぎわいのピークが見られました';
  }
  if (maxRaw >= 12) {
    return peakLabel
      ? `きのうは${peakLabel}ごろに人通りが増える場面がありました`
      : 'きのうは昼ごろに人通りが増える場面がありました';
  }
  return 'きのうはおおむね穏やかな流れでした';
};

const buildMultiLocationDayOverviewPanel = ({
  baseIso,
  spotResults = {},
  dayOffset = 0,
  panelTitle,
  buildSummary,
}) => {
  const iso = addDaysIso(baseIso, dayOffset);
  const locationRows = TV_PAST_WEEK_LOCATION_IDS.map((locationId) => {
    const { calendarMerged = [], detail = null } = spotResults[locationId] || {};
    const map = calibrateHourlyMapForTvSpot(
      locationId,
      resolveHourlyForIso(calendarMerged, detail, locationId, iso),
      { iso },
    );
    const slots = buildTwoHourSlots(map);
    return {
      locationId,
      label: getTvPastWeekLocationLabel(locationId),
      slots,
    };
  });

  const colSet = new Map();
  locationRows.forEach(({ slots }) => {
    slots.forEach((s) => {
      const key = s.timeLabel || s.label;
      if (key) colSet.set(key, key);
    });
  });
  const colLabels = [...colSet.keys()];
  const cells = locationRows.map(({ slots }) =>
    colLabels.map((col) => {
      const slot = slots.find((s) => (s.timeLabel || s.label) === col);
      return slot?.raw || 0;
    }),
  );

  return {
    panelTitle,
    rowHeader: '地点',
    rowLabels: locationRows.map((row) => row.label),
    colLabels,
    cells,
    locations: locationRows,
    summary: buildSummary(locationRows),
  };
};

const buildTodayOverviewSummary = (locationRows = []) => {
  if (!locationRows.length) return 'きょうのデータは確認中です';

  const allSlots = locationRows.flatMap((row) => row.slots || []);
  const raws = allSlots.map((s) => s.raw || 0).filter((v) => v > 0);
  if (!raws.length) return 'きょうのデータは確認中です';

  const maxRaw = Math.max(...raws);
  const peakLabel = findBusiestTwoHourLabel(allSlots);

  if (maxRaw >= 16) {
    return peakLabel
      ? `きょうは${peakLabel}ごろに人通りが増えやすい見込みです`
      : 'きょうはにぎわいのピークが見込まれます';
  }
  if (maxRaw >= 12) {
    return peakLabel
      ? `きょうは${peakLabel}ごろに人通りが増えやすい見込みです`
      : 'きょうは昼ごろに人通りが増えやすい見込みです';
  }
  return 'きょうは終日おおむね穏やかな見込みです';
};

const buildTomorrowOverviewSummary = (locationRows = []) => {
  if (!locationRows.length) return 'あしたのデータは確認中です';

  const allSlots = locationRows.flatMap((row) => row.slots || []);
  const raws = allSlots.map((s) => s.raw || 0).filter((v) => v > 0);
  if (!raws.length) return 'あしたのデータは確認中です';

  const maxRaw = Math.max(...raws);
  const peakLabel = findBusiestTwoHourLabel(allSlots);

  if (maxRaw >= 16) {
    return peakLabel
      ? `あしたは${peakLabel}ごろに人通りが増えやすい見込みです`
      : 'あしたはにぎわいのピークが見込まれます';
  }
  if (maxRaw >= 12) {
    return peakLabel
      ? `あしたは${peakLabel}ごろに人通りが増えやすい見込みです`
      : 'あしたは昼ごろに人通りが増えやすい見込みです';
  }
  return 'あしたは終日おおむね穏やかな見込みです';
};

/** 朝：主要4地点 × きょう（2時間刻み） */
export const buildTodayOverviewPanel = ({ baseIso, spotResults = {} }) =>
  buildMultiLocationDayOverviewPanel({
    baseIso,
    spotResults,
    dayOffset: 0,
    panelTitle: '今日の流れ · 主要4エリア',
    buildSummary: buildTodayOverviewSummary,
  });

/** 夜：主要4地点 × あした（2時間刻み）— 朝の「今日の流れ」から1日先 */
export const buildTomorrowOverviewPanel = ({ baseIso, spotResults = {} }) =>
  buildMultiLocationDayOverviewPanel({
    baseIso,
    spotResults,
    dayOffset: 1,
    panelTitle: '明日の流れ · 主要4エリア',
    buildSummary: buildTomorrowOverviewSummary,
  });

/** 主要4地点 × きのう（2時間刻み） */
export const buildYesterdayOverviewPanel = ({ baseIso, spotResults = {} }) =>
  buildMultiLocationDayOverviewPanel({
    baseIso,
    spotResults,
    dayOffset: -1,
    panelTitle: 'きのうの流れ · 主要4エリア',
    buildSummary: buildYesterdayOverviewSummary,
  });

/** 画面6：主要4地点 × 先週7日（1日平均）のコンパクト表 */
export const buildPastWeekOverviewPanel = ({ baseIso, spotResults = {} }) => {
  const locationRows = TV_PAST_WEEK_LOCATION_IDS.map((locationId) => {
    const { calendarMerged = [], detail = null } = spotResults[locationId] || {};
    const days = [];
    for (let i = 7; i >= 1; i -= 1) {
      const iso = addDaysIso(baseIso, -i);
      const map = calibrateHourlyMapForTvSpot(
        locationId,
        resolveHourlyForIso(calendarMerged, detail, locationId, iso),
        { iso },
      );
      const avg = Math.round(dailyAvgCongestion(map));
      days.push({
        iso,
        label: pastDayRowLabel(iso, baseIso),
        raw: avg,
      });
    }
    return {
      locationId,
      label: getTvPastWeekLocationLabel(locationId),
      days,
    };
  });

  const colLabels = locationRows[0]?.days.map((d) => d.label) ?? [];
  const cells = locationRows.map((row) => row.days.map((d) => d.raw));

  return {
    panelTitle: '先週の流れ',
    rowHeader: '地点',
    rowLabels: locationRows.map((row) => row.label),
    colLabels,
    cells,
    locations: locationRows,
    summary: buildPastWeekOverviewSummary(locationRows),
  };
};

export const buildStationPastWeekPanel = ({
  calendarData,
  detailData,
  baseIso,
  locationName = '駅前',
  imageSrc = TV_STATION_IMAGE,
  locationId = TV_STATION_ID,
}) => {
  const pastDays = [];
  for (let i = 7; i >= 1; i -= 1) {
    const iso = addDaysIso(baseIso, -i);
    const map = calibrateHourlyMapForTvSpot(
      locationId,
      resolveHourlyForIso(calendarData, detailData, locationId, iso),
      { iso },
    );
    pastDays.push({
      label: pastDayRowLabel(iso, baseIso),
      slots: buildTwoHourSlots(map),
    });
  }

  return {
    locationId,
    locationName,
    imageSrc,
    panelTitle: getTvSpotPanelTitles(locationId).pastWeek,
    pastDays,
    summary: buildPastWeekSummary(pastDays),
  };
};

export const buildStationWeekPanel = ({
  calendarData,
  detailData,
  baseIso,
  locationName = '駅前',
  imageSrc = TV_STATION_IMAGE,
  locationId = TV_STATION_ID,
}) => {
  const todayMap = calibrateHourlyMapForTvSpot(
    locationId,
    resolveHourlyForIso(calendarData, detailData, locationId, baseIso),
    { iso: baseIso },
  );
  const tomorrowMap = calibrateHourlyMapForTvSpot(
    locationId,
    resolveHourlyForIso(calendarData, detailData, locationId, addDaysIso(baseIso, 1)),
    { iso: addDaysIso(baseIso, 1) },
  );
  const dayAfterMap = calibrateHourlyMapForTvSpot(
    locationId,
    resolveHourlyForIso(calendarData, detailData, locationId, addDaysIso(baseIso, 2)),
    { iso: addDaysIso(baseIso, 2) },
  );

  const laterDays = [];
  for (let i = 3; i < 7; i += 1) {
    const iso = addDaysIso(baseIso, i);
    const map = calibrateHourlyMapForTvSpot(
      locationId,
      resolveHourlyForIso(calendarData, detailData, locationId, iso),
      { iso },
    );
    const slots = buildTwoHourSlots(map);
    const avg = dailyAvgCongestion(map);
    const tier = rawToCongestionTier(avg);
    laterDays.push({
      iso,
      dateLabel: shortDateChipLabel(iso),
      slots,
      ...tier,
    });
  }

  const summary = buildLaterTrend(calendarData, baseIso, locationId);

  return {
    locationId,
    locationName,
    imageSrc,
    panelTitle: getTvSpotPanelTitles(locationId).week,
    today: { label: '今日', slots: buildTwoHourSlots(todayMap) },
    tomorrow: { label: '明日', slots: buildTwoHourSlots(tomorrowMap) },
    dayAfter: { label: '明後日', slots: buildTwoHourSlots(dayAfterMap) },
    laterDays,
    summary,
  };
};

